import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import logger from '../utils/logger';

/**
 * Material Design 3 Theme Context
 * Implements production-ready theme switching with:
 * - System preference detection
 * - Manual override capability
 * - Persistent preference storage
 * - Real-time system preference updates
 */

const ThemeContext = createContext(null);
const themeLogger = logger.create('ThemeContext');

// Theme options
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

// Storage key for localStorage
const THEME_STORAGE_KEY = 'igo-theme-preference';

/**
 * Hook to access theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Get the resolved theme based on preference and system setting
 */
const getResolvedTheme = (preference) => {
  if (preference === THEMES.SYSTEM) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? THEMES.DARK 
      : THEMES.LIGHT;
  }
  return preference;
};

/**
 * Get initial theme preference from storage or default to system
 */
const getInitialTheme = () => {
  if (typeof window === 'undefined') return THEMES.SYSTEM;
  
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && Object.values(THEMES).includes(stored)) {
      return stored;
    }
  } catch (e) {
    themeLogger.warn('Failed to read theme preference from localStorage:', e);
  }
  
  return THEMES.SYSTEM;
};

/**
 * Apply theme class to document root
 */
const applyThemeToDOM = (resolvedTheme) => {
  const root = document.documentElement;
  const body = document.body;
  
  // Remove existing theme classes
  root.classList.remove('light', 'dark');
  body.classList.remove('light', 'dark');
  
  // Add the resolved theme class
  root.classList.add(resolvedTheme);
  body.classList.add(resolvedTheme);
  
  // Update meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      'content',
      resolvedTheme === THEMES.DARK ? '#121212' : '#F8F9FA'
    );
  }
  
  // Update color-scheme for native form controls
  root.style.colorScheme = resolvedTheme;
};

/**
 * Theme Provider Component
 */
export const ThemeProvider = ({ children }) => {
  // Theme preference (user's choice: light, dark, or system)
  const [themePreference, setThemePreference] = useState(getInitialTheme);
  
  // Resolved theme (actual applied theme: light or dark)
  const [resolvedTheme, setResolvedTheme] = useState(() => 
    getResolvedTheme(getInitialTheme())
  );

  // Apply theme to DOM whenever resolved theme changes
  const applyResolvedTheme = useCallback((newResolvedTheme) => {
    setResolvedTheme(newResolvedTheme);
    applyThemeToDOM(newResolvedTheme);
  }, []);

  // Handle system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemChange = (e) => {
      // Only update if user has selected 'system'
      if (themePreference === THEMES.SYSTEM) {
        const newResolvedTheme = e.matches ? THEMES.DARK : THEMES.LIGHT;
        applyResolvedTheme(newResolvedTheme);
      }
    };

    // Listen for system preference changes
    mediaQuery.addEventListener('change', handleSystemChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemChange);
    };
  }, [themePreference, applyResolvedTheme]);

  // Apply theme when preference changes
  useEffect(() => {
    const newResolvedTheme = getResolvedTheme(themePreference);
    applyResolvedTheme(newResolvedTheme);
    
    // Persist preference
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themePreference);
    } catch (e) {
      themeLogger.warn('Failed to save theme preference to localStorage:', e);
    }
  }, [themePreference, applyResolvedTheme]);

  // Initial theme application on mount
  useEffect(() => {
    applyThemeToDOM(getResolvedTheme(themePreference));
  }, [themePreference]);

  /**
   * Change the theme preference
   * @param {string} newTheme - 'light', 'dark', or 'system'
   */
  const setTheme = useCallback((newTheme) => {
    if (Object.values(THEMES).includes(newTheme)) {
      setThemePreference(newTheme);
    } else {
      themeLogger.warn(`Invalid theme: ${newTheme}. Use 'light', 'dark', or 'system'.`);
    }
  }, []);

  /**
   * Toggle between light and dark (ignores system)
   */
  const toggleTheme = useCallback(() => {
    const currentResolved = getResolvedTheme(themePreference);
    setThemePreference(currentResolved === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK);
  }, [themePreference]);

  /**
   * Check if system preference is dark
   */
  const systemPrefersDark = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  // Context value
  const value = useMemo(() => ({
    // Current preference (what user selected: light/dark/system)
    themePreference,
    
    // Actual applied theme (light or dark)
    theme: resolvedTheme,
    
    // Convenience booleans
    isDark: resolvedTheme === THEMES.DARK,
    isLight: resolvedTheme === THEMES.LIGHT,
    isSystem: themePreference === THEMES.SYSTEM,
    
    // System preference
    systemPrefersDark,
    
    // Actions
    setTheme,
    toggleTheme,
    
    // Theme constants
    THEMES
  }), [themePreference, resolvedTheme, systemPrefersDark, setTheme, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
